import { Box, ButtonBase, Typography } from "@mui/material";
import { PatternGlyph } from "@/shared/components/PatternGlyph";
import { layoutTokens, typographyTokens } from "@/shared/theme";
import type { PatternStat } from "../patterns.api";
import { formatMotif } from "../format";

export const PatternPicker = ({
  patterns,
  selected,
  onSelect,
}: {
  patterns: readonly Pick<PatternStat, "motif" | "count">[];
  selected: string;
  onSelect: (motif: string) => void;
}) => (
  <Box data-testid="pattern-picker" role="group" aria-label="Choose a pattern">
    <Typography
      sx={{
        fontSize: "caption.fontSize",
        fontWeight: "fontWeightBold",
        letterSpacing: typographyTokens.tracking.label,
        mb: 1,
      }}
    >
      CHOOSE A PATTERN · {patterns.length} FOUND
    </Typography>
    <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 1, scrollSnapType: "x proximity" }}>
      {patterns.map(({ motif, count }) => {
        const active = motif === selected;
        return (
          <ButtonBase
            key={motif}
            aria-label={`Inspect ${formatMotif(motif)}`}
            aria-pressed={active}
            onClick={() => onSelect(motif)}
            sx={{
              flex: "0 0 148px",
              minHeight: 136,
              p: 1.5,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
              textAlign: "left",
              scrollSnapAlign: "start",
              border: layoutTokens.dividerBorder,
              borderColor: active ? "primary.main" : "divider",
              borderRadius: 0,
              bgcolor: active ? "primary.main" : "background.default",
              color: active ? "primary.contrastText" : "text.primary",
              "&:hover": { borderColor: "primary.main" },
              "&:focus-visible": { outline: "2px solid", outlineColor: "text.primary", outlineOffset: -4 },
            }}
          >
            <Box sx={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center" }}>
              <Box sx={{ width: 32, height: 32, display: "grid", placeItems: "center" }}>
                <PatternGlyph motif={motif} />
              </Box>
              <Typography
                component="span"
                sx={{ fontSize: 10, fontWeight: "fontWeightBold", letterSpacing: typographyTokens.tracking.label }}
              >
                {active ? "VIEWING" : "EXPLORE"}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontWeight: "fontWeightBold", fontSize: "body2.fontSize", lineHeight: 1.2 }}>
                {formatMotif(motif)}
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: "caption.fontSize", opacity: 0.8 }}>
                {count} {count === 1 ? "moment" : "moments"}
              </Typography>
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  </Box>
);
